import { useCallback, useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { Mountain, MousePointerClick, Compass, TriangleRight, Copy, Check, Loader2, Sun, Flame } from "lucide-react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { MapboxMap } from "@/components/MapboxMap";
import { analyzePoint, sampleSlopeGrid } from "@/lib/terrain";

const fmt = (n, d = 0) => n.toLocaleString("en-US", { maximumFractionDigits: d, minimumFractionDigits: d });

const SLOPE_PAINT = {
  // weight = steepness (0–45° clamped in sampleSlopeGrid); with a uniform sample grid,
  // density ∝ steepness, so the color ramp reads as gentle → steep
  "heatmap-weight": ["interpolate", ["linear"], ["get", "slope"], 0, 0, 6, 0.12, 14, 0.4, 24, 0.75, 32, 1],
  "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 11, 1, 14, 1.6],
  "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 10, 16, 12, 26, 14, 46],
  "heatmap-opacity": 0.85,
  "heatmap-color": [
    "interpolate", ["linear"], ["heatmap-density"],
    0, "rgba(0,0,0,0)",
    0.12, "rgba(46,125,70,0.35)",
    0.35, "rgba(183,118,31,0.5)",
    0.65, "rgba(178,74,58,0.7)",
    1, "rgba(120,30,20,0.85)",
  ],
};

export default function TerrainAnalysis() {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null); // only to retrigger the slope-layer effect
  const [result, setResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [hint, setHint] = useState(null);
  const [copied, setCopied] = useState(false);
  const [slopeOn, setSlopeOn] = useState(false);
  const [slopeHint, setSlopeHint] = useState(null);

  const removeSlope = useCallback((m) => {
    if (!m || !m.getStyle()) return;  // map already removed (remount) — nothing to clean
    if (m.getLayer("slope-heat")) m.removeLayer("slope-heat");
    if (m.getSource("slope-grid")) m.removeSource("slope-grid");
  }, []);

  const buildSlope = useCallback((m) => {
    if (!m || !m.getStyle()) return;
    if (m.getZoom() < 10.5) {
      setSlopeHint("Zoom in closer to shade slope — elevation tiles need a tighter view.");
      removeSlope(m);
      return;
    }
    const b = m.getBounds();
    const { features, coverage } = sampleSlopeGrid(
      (pt) => m.queryTerrainElevation(pt, { exaggerated: false }),
      { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() },
    );
    const data = { type: "FeatureCollection", features };
    if (m.getSource("slope-grid")) {
      m.getSource("slope-grid").setData(data);
    } else {
      m.addSource("slope-grid", { type: "geojson", data });
      m.addLayer({ id: "slope-heat", type: "heatmap", source: "slope-grid", paint: SLOPE_PAINT });
    }
    setSlopeHint(coverage < 0.6 ? "Some elevation tiles aren't loaded at this zoom — zoom in for complete shading." : null);
  }, [removeSlope]);

  // Slope shading lifecycle: rebuild after every move while enabled; clean up when off.
  // Heatmap layers render flat, so shading forces a top-down 2D view (terrain visual off).
  useEffect(() => {
    if (!mapInstance) return undefined;
    if (!slopeOn) { removeSlope(mapInstance); setSlopeHint(null); return undefined; }
    const rebuild = () => buildSlope(mapInstance);
    mapInstance.once("idle", rebuild);
    mapInstance.on("moveend", rebuild);
    return () => { mapInstance.off("moveend", rebuild); removeSlope(mapInstance); };
  }, [mapInstance, slopeOn, buildSlope, removeSlope]);

  const analyzeAt = useCallback((lngLat) => {
    const m = mapRef.current; if (!m) return;
    setAnalyzing(true);
    setHint(null);
    const run = () => {
      const r = analyzePoint((pt) => m.queryTerrainElevation(pt, { exaggerated: false }), lngLat.lng, lngLat.lat);
      if (r) { setResult(r); setAnalyzing(false); return true; }
      return false;
    };
    if (run()) return;
    setHint("Zooming in to load elevation data…");
    m.flyTo({ center: [lngLat.lng, lngLat.lat], zoom: Math.max(m.getZoom(), 13.2), speed: 1.6 });
    m.once("idle", () => {
      if (run()) setHint(null);
      else { setAnalyzing(false); setHint("Elevation data isn't available here — try zooming in a little more."); }
    });
  }, []);

  const placeMarker = useCallback((lngLat) => {
    const m = mapRef.current; if (!m) return;
    if (!markerRef.current) {
      markerRef.current = new mapboxgl.Marker({ color: "#E2591F", draggable: true })
        .setLngLat(lngLat).addTo(m);
      markerRef.current.on("dragend", () => analyzeAt(markerRef.current.getLngLat()));
    } else {
      markerRef.current.setLngLat(lngLat);
    }
  }, [analyzeAt]);

  const handleLoad = useCallback((m) => {
    mapRef.current = m;
    setMapInstance(m);
    markerRef.current = null;           // a remount destroyed the old map + marker
    m.on("click", (e) => { placeMarker(e.lngLat); analyzeAt(e.lngLat); });
    setResult((prev) => {
      if (prev) placeMarker({ lng: prev.lng, lat: prev.lat });  // restore pin after remount
      return prev;
    });
  }, [placeMarker, analyzeAt]);

  const copyCoords = () => {
    if (!result) return;
    navigator.clipboard.writeText(`${result.lat.toFixed(6)}, ${result.lng.toFixed(6)}`);
    setCopied(true);
    toast.success("Coordinates copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="bg-paper min-h-screen text-charcoal flex flex-col" data-testid="terrain-page">
      <SiteHeader />
      <main className="flex-1 max-w-[1400px] w-full mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2 text-forest">
              <Mountain size={18} className="text-blaze" />
              <h1 className="font-serif text-3xl sm:text-4xl tracking-tight">Terrain analysis</h1>
            </div>
            <p className="text-sm text-charcoal/55 mt-1 max-w-2xl">
              Click any point to read its elevation, slope and aspect — the three numbers that decide
              where game beds, feeds and moves. Drag the marker to fine-tune a stand site.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSlopeOn(!slopeOn)}
              className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 border transition-colors ${slopeOn ? "bg-blaze text-white border-blaze" : "bg-white text-charcoal/70 border-saddle/25 hover:border-blaze/50"}`}
              data-testid="terrain-slope-toggle"
            >
              <Flame size={15} /> Slope shading {slopeOn ? "on" : "off"}
            </button>
            <p className="hidden sm:inline-flex items-center gap-1.5 text-xs text-charcoal/50 border border-saddle/25 bg-white px-3 py-2">
              <MousePointerClick size={13} className="text-blaze" /> Click the map to analyze
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start">
          <div className="border border-saddle/25 bg-white p-1.5">
            <MapboxMap
              center={[-106.82, 39.19]}
              zoom={12.5}
              pitch={slopeOn ? 0 : 55}
              enableTerrain
              terrainExaggeration={slopeOn ? 0 : 1}
              height={600}
              onLoad={handleLoad}
              testId="terrain-map"
            />
            {slopeOn && (
              <div className="flex items-center justify-between gap-3 flex-wrap px-2 py-1.5 border-t border-saddle/15" data-testid="slope-legend">
                <div className="flex items-center gap-2 text-[11px] text-charcoal/55">
                  <span>gentle</span>
                  <span className="inline-block w-28 h-2 rounded-full" style={{ background: "linear-gradient(90deg, rgba(46,125,70,0.35), rgba(183,118,31,0.55), rgba(178,74,58,0.75), rgba(120,30,20,0.9))" }} />
                  <span>steep</span>
                </div>
                <span className="text-[11px] text-charcoal/45">Top-down view while shading · flat spots inside steep country = benches</span>
              </div>
            )}
          </div>

          <aside className="space-y-3" data-testid="terrain-panel">
            {analyzing && (
              <div className="bg-white border border-saddle/20 p-5 flex items-center gap-2 text-sm text-charcoal/60" data-testid="terrain-analyzing">
                <Loader2 size={16} className="animate-spin text-blaze" /> {hint || "Reading the terrain…"}
              </div>
            )}
            {!analyzing && hint && (
              <div className="bg-white border border-[#B7761F]/30 p-4 text-sm text-[#B7761F]" data-testid="terrain-hint">{hint}</div>
            )}
            {slopeOn && slopeHint && (
              <div className="bg-white border border-[#B7761F]/30 p-4 text-sm text-[#B7761F]" data-testid="slope-hint">{slopeHint}</div>
            )}
            {!analyzing && !result && !hint && (
              <div className="bg-white border border-dashed border-saddle/30 p-6 text-center" data-testid="terrain-empty">
                <Compass size={22} className="mx-auto text-saddle/50" />
                <p className="font-serif text-lg text-forest mt-2">No point selected yet</p>
                <p className="text-sm text-charcoal/55 mt-1">Click anywhere on the map — ridge tops, saddles, benches and south-facing fingers are good places to start.</p>
              </div>
            )}
            {result && !analyzing && (
              <div className="bg-white border border-saddle/20" data-testid="terrain-results">
                <div className="px-4 py-3 border-b border-saddle/15 flex items-center justify-between">
                  <p className="hr-overline text-charcoal/50">Site reading</p>
                  <span className="text-[10px] text-charcoal/40">Mapbox Terrain DEM · ~30 m resolution</span>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <p className="hr-overline text-charcoal/45 flex items-center gap-1.5"><Mountain size={11} /> Elevation</p>
                    <p className="font-serif text-3xl text-forest mt-1 tabular-nums" data-testid="terrain-elevation">
                      {fmt(result.elevationFt)} ft
                      <span className="text-base text-charcoal/45 font-sans ml-2">{fmt(result.elevationM)} m</span>
                    </p>
                  </div>
                  <div>
                    <p className="hr-overline text-charcoal/45 flex items-center gap-1.5"><TriangleRight size={11} /> Slope</p>
                    <p className="font-serif text-3xl text-forest mt-1 tabular-nums" data-testid="terrain-slope">
                      {fmt(result.slopeDeg, 1)}°
                      <span className="text-base text-charcoal/45 font-sans ml-2">{fmt(result.slopePct)}% grade</span>
                    </p>
                    <p className="text-[11px] text-charcoal/50 mt-0.5">
                      {result.slopeDeg < 5 ? "Nearly flat — easy travel, watch thermals on calm mornings" :
                       result.slopeDeg < 15 ? "Gentle — benches and fingers like this hold feeding game" :
                       result.slopeDeg < 30 ? "Moderate — classic sidehill travel grade" :
                       "Steep — expect game to contour around rather than cross"}
                    </p>
                  </div>
                  <div>
                    <p className="hr-overline text-charcoal/45 flex items-center gap-1.5"><Sun size={11} /> Aspect</p>
                    <p className="font-serif text-3xl text-forest mt-1 tabular-nums" data-testid="terrain-aspect">
                      {result.aspectCardinal}
                      {result.aspectDeg != null && <span className="text-base text-charcoal/45 font-sans ml-2">{fmt(result.aspectDeg)}° downslope</span>}
                    </p>
                    <p className="text-[11px] text-charcoal/50 mt-0.5" data-testid="terrain-aspect-note">{result.aspectNote}</p>
                  </div>
                  <div className="pt-2 border-t border-saddle/15 flex items-center justify-between gap-2">
                    <p className="text-xs text-charcoal/55 tabular-nums" data-testid="terrain-coords">{result.lat.toFixed(6)}, {result.lng.toFixed(6)}</p>
                    <button onClick={copyCoords} className="inline-flex items-center gap-1 text-xs font-semibold text-blaze hover:underline" data-testid="terrain-copy">
                      {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <p className="text-[11px] text-charcoal/45 px-1">
              Elevation is sampled from Mapbox Terrain DEM (~30 m cells). Read slope/aspect as stand-site
              scouting guidance — always confirm conditions in the field.
            </p>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
